import { CandidateScoringStatus, JobOffer, MatchingPriorities, Skill } from "../models/job";

/**
 * Types for job service
 */

// Enums
export enum JobStatus {
  DRAFT = "draft",
  ANALYZING = "analyzing",
  ACTIVE = "active",
  INTERVIEWING = "interviewing",
  CLOSED = "closed"
}

export enum DocumentType {
  CV = "cv",
  COVER_LETTER = "cover_letter",
  JOB_DESCRIPTION = "job_description",
  COMPANY_PROFILE = "company_profile",
  OTHER = "other"
}

export enum ProcessingStatus {
  PENDING = "pending",
  PROCESSING = "processing",
  COMPLETED = "completed",
  FAILED = "failed"
}

// API Response Types
export interface ApiJobOffer {
  id: string;
  title: string;
  description: string;
  education_requirements?: string;
  skills?: Array<string | Skill>;
  coding_languages?: string[];
  frameworks_libraries?: string[];
  business_knowledge?: string[];
  soft_skills?: string[];
  years_of_experience?: number;
  custom_instructions?: string;
  is_active: boolean;
  status: JobStatus;
  created_by_id: string;
  creator_email?: string;
  created_at: string;
  updated_at?: string;
  documents?: ApiDocument[];
  remote_policy?: string;
  contract_type?: string;
  location?: string;
  required_degree?: string;
  company_name?: string;
  company_sector?: string;
  company_size?: number;
  company_description?: string;
  mission_role_in_job?: string;
  authorize_cv_upload?: boolean;
  enable_cv_auto_interview?: boolean;
  cv_interview_threshold?: number;
  brief_exam?: { technical?: string };
  candidate_count?: number;
  requires_review?: boolean;
  source?: string;
  /** Present on public job endpoints — tenant UI language. */
  locale?: string;
  /** Public workspace logo URL (storage-backed when configured). */
  workspace_logo_url?: string;
  /** Optional workspace branding colors for public candidate pages. */
  workspace_primary_color?: string | null;
  workspace_secondary_color?: string | null;
  matching_priorities?: MatchingPriorities;
}

export interface ApiDocument {
  id: string;
  job_offer_id: string;
  document_type: DocumentType;
  file_name: string;
  file_path: string;
  file_size: number;
  mime_type: string;
  extracted_data?: Record<string, unknown>;
  processing_status: string;
  created_at: string;
  updated_at?: string;
}

// Frontend Types
export interface Document {
  id: string;
  jobOfferId: string;
  documentType: DocumentType;
  fileName: string;
  filePath: string;
  fileSize: number;
  mimeType: string;
  extractedData?: Record<string, unknown>;
  processingStatus: string;
  createdAt: string;
  updatedAt?: string;
}

export interface JobOffersResponse {
  job_offers: JobOffer[];
  total: number;
}

export interface PaginatedJobOffersResponse {
  items: JobOffer[];
  total: number;
  page: number;
  pages: number;
  size: number;
}

export interface JobOfferCreateData {
  title: string;
  description: string;
  is_active?: boolean;
  status?: JobStatus;
  matching_priorities?: MatchingPriorities;
}

/**
 * Explicit PATCH side-effect strategy (mirrors backend `edit_strategy`).
 * Names match Edit Validate card titles (“Update job” / “Update job, AI test and rescore…”).
 *
 * - omit / `undefined` — legacy callers: auto-rescore on fingerprint change, no exam regen
 * - `update_job` — save job fields only; never rescore or regenerate exam
 * - `update_job_exam_and_rescore` — on fingerprint change: CV rescore + forced exam regen
 *
 * The Edit job modal always sends an explicit value (never omits).
 */
export type JobEditStrategy = 'update_job' | 'update_job_exam_and_rescore';

/** PATCH /offers/{id} body (snake_case, as the API expects). `null` clears a field. */
export interface JobOfferUpdateData {
  title?: string;
  description?: string;
  education_requirements?: string | null;
  skills?: Array<string | Skill>;
  coding_languages?: string[];
  frameworks_libraries?: string[];
  business_knowledge?: string[];
  soft_skills?: string[];
  years_of_experience?: number | null;
  custom_instructions?: string | null;
  is_active?: boolean;
  status?: JobStatus;
  remote_policy?: string | null;
  contract_type?: string | null;
  location?: string | null;
  required_degree?: string | null;
  company_name?: string | null;
  company_sector?: string | null;
  company_size?: number | null;
  company_description?: string | null;
  mission_role_in_job?: string | null;
  matching_priorities?: MatchingPriorities;
  /** Request-only; never persisted. See `JobEditStrategy`. */
  edit_strategy?: JobEditStrategy;
}

/** What a job edit means for candidate scores / exam regeneration. */
export interface JobRescoreSummary {
  scoring_changed: boolean;
  candidates_to_rescore: number;
  rescore_scheduled?: boolean;
  exam_regeneration_scheduled?: boolean;
}

export interface DocumentUploadResponse {
  document_id: string;
  job_offer_id: string;
  file_name: string;
  processing_status: string;
  message: string;
  locale?: string;
}

export interface JobStatistics {
  // Job-related statistics
  total_jobs: number;
  active_jobs: number;
  completed_jobs: number;
  draft_jobs: number;
  interviewing_jobs: number;
  analyzing_jobs: number;
  
  // Candidate-related statistics
  total_candidates: number;
  interviewing_candidates: number;   // Candidates currently being interviewed
  interviewed_candidates: number;    // Candidates who completed interviews (interviewed + accepted + rejected + hired)
  completed_interviews: number;      // For backward compatibility - same as interviewed_candidates
  accepted_candidates: number;       // Selected/accepted candidates
  rejected_candidates: number;       // Rejected candidates
  hired_candidates: number;          // Hired candidaates
  pending_candidates: number;        // Candidates still pending
  cvs_uploaded: number;
  total_companies: number;
  avg_technical_score: number | null;
}

export interface CandidateApiResponse {
  id: string;
  talent_id: string;
  talent_pool_profile_id: string | null;
  job_offer_id: string;
  source?: string;
  score: number | null;
  talent_name: string;
  talent_email: string;
  talent_linkedin_url: string | null;
  enhanced_data: Record<string, unknown> | null;
  created_at: string;
  updated_at: string | null;
  has_assessment_available: boolean;
  has_valid_email: boolean;
  interview_invitation_token: string | null;
  interview_id: string | null;
  interview_status: string | null;
  interview_expires_at: string | null;
  pipeline_stage_id?: string | null;
  pipeline_stage_label?: string | null;
  pipeline_stage_semantic?: string | null;
  scoring_status?: CandidateScoringStatus | null;
  stage_position?: string | null;
}

export interface PipelineStageApiResponse {
  id: string;
  label: string;
  position: number;
  is_default: boolean;
  stage_type: string;
  automation_enabled?: boolean;
  has_automation_rule?: boolean;
}

export interface PipelineApiResponse {
  id: string;
  job_offer_id: string;
  stages: PipelineStageApiResponse[];
}

export interface MoveCandidateApiResponse {
  transition_id: string;
  actions_queued: number;
  pipeline_stage_id: string;
  stage_position: string;
  position_error?: string | null;
}

export interface CandidatePositionApiResponse {
  id: string;
  pipeline_stage_id: string;
  stage_position: string;
}

export interface SkillLevel {
  level: number;
  explanation: string;
}

export interface TechnicalAssessment {
  // v1 fields
  algorithmic?: SkillLevel;
  clean_code?: SkillLevel;
  core_concepts_strengths?: SkillLevel;
  core_concepts_weaknesses?: SkillLevel;
  tradeoffs?: SkillLevel;
  debugging?: SkillLevel;
  ai_tools?: SkillLevel;
  recommendation?: string;
  overall_tech_percent?: number;
  // v2 fields
  version?: string;
  strengths?: string[];
  weaknesses?: string[];
  overall_score?: number;
  skill_breakdown?: {
    core_concepts_strengths?: {
      level?: number;
      explanation?: string;
    };
    core_concepts_weaknesses?: {
      level?: number;
      explanation?: string;
    };
    tradeoffs?: {
      level?: number;
      explanation?: string;
    };
    algorithmic?: {
      level?: number;
      explanation?: string;
    };
    clean_code?: {
      level?: number;
      explanation?: string;
    };
    debugging?: {
      level?: number;
      explanation?: string;
    };
    ai_tools?: {
      level?: number;
      explanation?: string;
    };
    [key: string]: { level?: number; explanation?: string } | undefined;
  };
}

export interface SoftSkillsAssessment {
  // v1 fields
  communication?: SkillLevel;
  team_spirit?: SkillLevel;
  growth_mindset?: SkillLevel;
  self_awareness?: SkillLevel;
  adaptability?: SkillLevel;
  case_study?: SkillLevel;
  recommendation?: string;
  overall_soft_skills_percent?: number;
  // v2 fields
  version?: string;
  strengths?: string[];
  weaknesses?: string[];
  overall_score?: number;
  skill_breakdown?: {
    communication?: {
      level?: number;
      explanation?: string;
    };
    team_spirit?: {
      level?: number;
      explanation?: string;
    };
    growth_mindset?: {
      level?: number;
      explanation?: string;
    };
    self_awareness?: {
      level?: number;
      explanation?: string;
    };
    adaptability?: {
      level?: number;
      explanation?: string;
    };
    [key: string]: { level?: number; explanation?: string } | undefined;
  };
  [key: string]: any;
}

// Cheating Detection Types
export interface VideoTime {
  minute: number;
  second: number;
}

export interface SuspectedSegment {
  start_time: VideoTime;
  end_time: VideoTime;
  explanation: string;
  suspection_level: 'low' | 'medium' | 'high';
}

export interface QuestionCheatingDetection {
  suspected_cheating_segments: SuspectedSegment[];
  cheatinglikelihood_score: number;
  overall_explanation: string;
  /** Present when an admin manually cleared this question's flag after review.
   *  Preserves the original detection values for auditability. */
  cleared_by_admin?: {
    cleared_by: string;
    cleared_at: string;
    original_cheatinglikelihood_score?: number;
    original_suspected_cheating_segments?: SuspectedSegment[];
  };
}

// Assessment Types
export interface QuestionScore {
  question_index: number;
  skill_area: string;
  section: string;  // "Technical Skills" or "Soft Skills"
  question_type: string;  // "spoken", "code-read-only", "code-write"
  score: number;  // 1-10
  anchor_level: string;  // "low", "mid", "high"
  evidence: string;
  justification: string;
  is_personalized: boolean;
  is_inferred?: boolean;
  score_explanation?: string;
}

export interface SkillScore {
  skill_area: string;
  section: string;
  average_score: number;
  question_count: number;
  question_scores: QuestionScore[];
}

export interface SectionScore {
  section: string;
  average_score: number;
  skill_scores: SkillScore[];
}

export interface BARSAnchors {
  skill_area: string;
  low_performance: string;
  mid_performance: string;
  high_performance: string;
}

export interface SkippedQuestion {
  question_index: number;
  skill_area: string;
  section: string;
  reason: string;
}

export interface CandidateAssessmentSummary {
  candidate_id: string;
  interview_id?: string;
  personality_traits?: string[] | null;
  final_recommendation?: string;
  fit_score?: number;
  is_processing: boolean;
  technical_assessment?: TechnicalAssessment;
  soft_skills_assessment?: SoftSkillsAssessment;
  // v2 fields
  fit_assessment?: {
    fit_score?: number;
    executive_summary?: string;
    key_strengths_aligning_with_role?: string[];
    areas_of_concern_and_development?: string[];
    specific_recommendations_for_hiring?: string[];
    risk_assessment_and_mitigation_stratgies?: string;
    expected_onboarding_and_rampup_timeline?: string;
  };
  language_assessment?: {
    cefr_level?: string;
    language_feedback?: string;
    spoken_language?: string;
  };
  // Cheating detection fields
  cheating_detection?: Record<string, QuestionCheatingDetection>;
  has_cheating_flag?: boolean;
  // Assessment fields
  scoring_version?: string;  // "v3" for new, "v2" for migrated
  per_question_scores?: QuestionScore[];
  skill_scores?: Record<string, SkillScore>;
  bars_anchors?: Record<string, BARSAnchors>;
  sections?: SectionScore[];
  skipped_questions?: SkippedQuestion[];
  psychometric_results?: {
    mbti?: {
      type: string;
      scores: Record<string, number>;
      percentages: Record<string, number>;
      description?: string;
      title?: string;
      executive_summary?: string;
      type_explanation?: Record<string, string>;
      strengths?: string[];
      watch_outs?: string[];
    };
    disc?: {
      scores: Record<string, number>;
      percentages: Record<string, number>;
      primary: string;
      secondary: string;
      language?: string;
      title?: string;
      description?: string;
      strengths?: string[];
      challenges?: string[];
      plot?: {
        resultant_angle: number;
        resultant_magnitude: number;
        angles?: Record<string, number>;
        categories?: string[];
      };
    };
  } | null;
}

export interface CandidateStatusUpdate {
  id: string;
  pipeline_stage_id: string;
  pipeline_stage_semantic: string;
  message: string;
}

export interface ExamConfig {
  technical: {
    coreConceptsStrengths: boolean;
    coreConceptsWeaknesses: boolean;
    algorithmic: boolean;
    cleanCode: boolean;
    debugging: boolean;
    tradeoffs: boolean;
    ai_tools: boolean;
  };
  softSkills: {
    communication: boolean;
    teamSpirit: boolean;
    selfAwareness: boolean;
    growthMindset: boolean;
    adaptability: boolean;
    caseStudy: boolean;
    lastExperience: boolean;
  };
}

export interface PlannedQuestionItem {
  index: number;
  question: string;
  time: number;
  question_type: string;
  question_kind: string;
  screen_recording: boolean;
  read_ide: boolean;
  write_ide: boolean;
  audio_file: string;
  criteria: string;
  coding_language?: string;
}

export interface JobPlannedQuestionsResponse {
  interview_id: string;
  candidate_id: string;
  candidate_name: string;
  candidate_email: string;
  interview_status: string;
  questions: PlannedQuestionItem[];
  created_at: string;
}

export interface JobPlannedQuestionsListResponse {
  job_id: string;
  job_title: string;
  planned_questions: JobPlannedQuestionsResponse[];
}

// Candidate Questions and Answers Types
export interface CandidateQuestionAnswer {
  question_text: string;
  question_type: string;
  question_kind: string;
  criteria?: string;
  candidate_answer: string;
  candidate_code: string;
  code_snippet?: string;
  coding_language?: string;
  time_allocated_minutes?: number | null;
  time_spent_seconds?: number | null;
  skill_area?: string;
  section?: string;
}

export interface CandidateQuestionsAnswersResponse {
  candidate_id: string;
  candidate_name: string;
  candidate_email: string;
  interview_id: string;
  interview_status: string;
  questions_answers: CandidateQuestionAnswer[];
}

export interface ResumeSearchFilters {
  technical_skills?: string[];
  title?: string;
  previous_sectors_fields?: string[];
  education?: string[];
  languages?: string[];
  min_experience?: number;
  job_offer_id?: string;
  unlinked_only?: boolean;
  exclude_job_offer_id?: string;
}

export interface ResumeSearchRequest {
  filters: ResumeSearchFilters;
  page?: number;
  page_size?: number;
}

export interface ResumeItem {
  id: string;
  email: string;
  file_name: string;
  years_of_experience?: number;
  extracted_data: {
    document_name: string;
    document_type: string;
    extracted_info: {
      email: string;
      first_name: string;
      last_name: string;
      title: string;
      experience: Array<{
        job_title: string;
        company: string;
        duration: string;
      }>;
      technical_skills: string[];
      previous_sectors_fields: string[];
      education: string[];
      languages: string[];
    };
  };
  created_at: string;
  associated_jobs?: Array<{
    job_id: string;
    job_title: string;
  }>;
}

export interface ResumeSearchResponse {
  resumes: ResumeItem[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface LinkResumeRequest {
  document_ids: string[];
  job_offer_id: string;  // Sent in body for validation, but path parameter takes precedence
}

export interface LinkedDocumentInfo {
  document_id: string;
  file_name: string;
  action: 'linked_and_processing' | 'duplicated_and_processing';
}

export interface LinkResumeError {
  document_id: string;
  error: string;
}

export interface LinkResumeResponse {
  message: string;
  linked_documents: LinkedDocumentInfo[];
  errors: LinkResumeError[];
  total_requested: number;
  total_linked: number;
  total_errors: number;
}

export interface ResumeUploadResponse {
  id: string;
  file_name: string;
  file_size: number;
  status: string;
}
